import { AutosizeTextarea } from "../ui/autoresizeTextArea";
import { Button } from "../ui/button";
import { Card, CardHeader, CardContent, CardTitle } from "../ui/card";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

import {
    WithLoggedInAuthInfoProps,
    withRequiredAuthInfo,
} from "@propelauth/react";
import React, { useContext, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../ui/use-toast";

import { getQaItem, updateQAItem } from "./qa_api";
import { QAModel } from "./QAModel";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";

type EditQAProps = WithLoggedInAuthInfoProps & {
    key?: string;
    qa_id: string;
    onSave?: (data: QAModel) => void;
    onCancel: () => void;
};

const EditQA: React.FC<EditQAProps> = (props) => {
    const { project_id } = useParams<{ project_id: string }>();
    const { qa_id, accessToken } = props;
    const { toast } = useToast();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    const form = useForm<QAModel>({
        defaultValues: {
            title: "",
            body: "",
        },
        mode: "onChange",
    });

    useEffect(() => {
        if (project_id && qa_id) {
            getQaItem(project_id, qa_id, accessToken)
                .then((data) => {
                    form.reset(data);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching QA item:", error);
                    toast({
                        title: "Error",
                        description: "Failed to load the item.",
                        variant: "destructive",
                    });
                    setIsLoading(false);
                });
        }
    }, [project_id, qa_id, accessToken, form, toast]);

    if (!project_id) {
        return <div>No ProjectID</div>;
    }
    if (isLoading) {
        return <div><BaseLoadingSpinner/></div>;
    }

    const onSave: SubmitHandler<QAModel> = (data: QAModel) => {
        updateQAItem(project_id, qa_id, accessToken, data)
            .then(() => {
                toast({
                    title: "Success!",
                    description: "Item updated successfully.",
                    variant: "default",
                });
                if (props.onSave) {
                    props.onSave(data);
                } else {
                    navigate(-1); // Navigate back
                }
            })
            .catch((error) => {
                console.error("Error updating QA item:", error);
                toast({
                    title: "Error",
                    description: error.message || "Failed to update the item.",
                    variant: "destructive",
                });
            });
    };

    return (
        <div className="container mb-5">
            <Card>
                <Form {...form}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            form.handleSubmit(onSave)();
                        }}
                    >
                        <CardHeader>
                            <CardTitle>Edit Comment</CardTitle>
                            <div className="flex justify-between items-center">
                                <Button
                                    onClick={() => props.onCancel()}
                                    type="button"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant={"secondary"}
                                    type="submit"
                                    disabled={!form.formState.isValid}
                                >
                                    Save
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <FormField
                                control={form.control}
                                name="title"
                                rules={{ required: "Title is required" }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Title</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter title"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="body"
                                rules={{ required: "Body is required" }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Body</FormLabel>
                                        <FormControl>
                                            <AutosizeTextarea
                                                placeholder="Enter body"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </CardContent>
                    </form>
                </Form>
            </Card>
        </div>
    );
};

export default withRequiredAuthInfo(EditQA);
